import React, { useState, useEffect } from 'react';
import { CircleDot, Plus, X, Check, Pencil, Trash2 } from 'lucide-react';
import { LocalMatch, LocalMatchday, CreateLocalMatchRequest, UpdateLocalMatchRequest, LocalMatchStatus } from '../../types/localMatchday';
import { LocalMatchdayApi, LocalMatchApi } from '../../services/localMatchdayApi';

const LocalMatchesDataTable: React.FC = () => {
  const [matches, setMatches] = useState<LocalMatch[]>([]);
  const [matchdays, setMatchdays] = useState<LocalMatchday[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editData, setEditData] = useState<Partial<LocalMatch>>({});
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [newMatchData, setNewMatchData] = useState<Partial<LocalMatch>>({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [matchesData, matchdaysData] = await Promise.all([
        LocalMatchApi.getAllLocalMatches(true),
        LocalMatchdayApi.getAllLocalMatchdays(true),
      ]);
      setMatches(matchesData);
      setMatchdays(matchdaysData);
      setError(null);
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (match: LocalMatch) => {
    setEditingId(match.id);
    setEditData({ ...match });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditData({});
  };

  const handleSave = async (id: number) => {
    try {
      const updateRequest: UpdateLocalMatchRequest = {
        homeTeam: editData.homeTeam!,
        awayTeam: editData.awayTeam!,
        scheduledDate: editData.scheduledDate!,
        actualDate: editData.actualDate,
        location: editData.location,
        referee: editData.referee,
        duration: editData.duration,
        notes: editData.notes,
        status: typeof editData.status === 'number' ? editData.status : LocalMatchStatus.Scheduled,
        homeScore: editData.homeScore,
        awayScore: editData.awayScore,
        isActive: editData.isActive ?? true,
      };
      await LocalMatchApi.updateLocalMatch(id, updateRequest);
      await loadData();
      setEditingId(null);
      setEditData({});
      setError(null);
    } catch (err) {
      console.error('Error updating match:', err);
      setError('Fehler beim Aktualisieren');
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await LocalMatchApi.deleteLocalMatch(id);
      await loadData();
      setDeleteConfirm(null);
      setError(null);
    } catch (err) {
      console.error('Error deleting match:', err);
      setError('Fehler beim Löschen');
    }
  };

  const handleStartCreate = () => {
    setIsCreating(true);
    const now = new Date().toISOString();
    setNewMatchData({
      homeTeam: '',
      awayTeam: '',
      scheduledDate: now,
      status: LocalMatchStatus.Scheduled,
      localMatchdayId: 0,
      isActive: true,
    });
  };

  const handleCancelCreate = () => {
    setIsCreating(false);
    setNewMatchData({});
  };

  const handleSaveNew = async () => {
    try {
      // Validierung
      if (!newMatchData.homeTeam || !newMatchData.homeTeam.trim()) {
        setError('Bitte geben Sie ein Heimteam ein');
        return;
      }
      if (!newMatchData.awayTeam || !newMatchData.awayTeam.trim()) {
        setError('Bitte geben Sie ein Auswärtsteam ein');
        return;
      }
      if (newMatchData.homeTeam === newMatchData.awayTeam) {
        setError('Heim- und Auswärtsteam müssen unterschiedlich sein');
        return;
      }
      if (!newMatchData.scheduledDate) {
        setError('Bitte geben Sie ein geplantes Datum ein');
        return;
      }
      if (!newMatchData.localMatchdayId || newMatchData.localMatchdayId === 0) {
        setError('Bitte wählen Sie einen Spieltag aus');
        return;
      }

      const createRequest: CreateLocalMatchRequest = {
        homeTeam: newMatchData.homeTeam,
        awayTeam: newMatchData.awayTeam,
        scheduledDate: newMatchData.scheduledDate,
        actualDate: newMatchData.actualDate,
        location: newMatchData.location,
        referee: newMatchData.referee,
        duration: newMatchData.duration,
        notes: newMatchData.notes,
        status: typeof newMatchData.status === 'number' ? newMatchData.status : LocalMatchStatus.Scheduled,
        localMatchdayId: newMatchData.localMatchdayId,
        homeScore: newMatchData.homeScore,
        awayScore: newMatchData.awayScore,
        isActive: newMatchData.isActive ?? true,
      };

      await LocalMatchApi.createLocalMatch(createRequest);
      await loadData();
      setIsCreating(false);
      setNewMatchData({});
      setError(null);
    } catch (err) {
      console.error('Error creating match:', err);
      setError('Fehler beim Erstellen des Spiels');
    }
  };

  const formatDateTime = (date: string | undefined) => {
    if (!date) return '';
    return new Date(date).toISOString().slice(0, 16);
  };

  const parseDateTime = (dateTimeString: string) => {
    if (!dateTimeString) return undefined;
    return new Date(dateTimeString).toISOString();
  };

  const getStatusText = (status: LocalMatchStatus | string | number | null | undefined) => {
    if (status === null || status === undefined) return 'Unbekannt';
    const statusNum = typeof status === 'number' ? status : parseInt(status.toString());
    switch (statusNum) {
      case LocalMatchStatus.Scheduled: return 'Geplant';
      case LocalMatchStatus.Live: return 'Live';
      case LocalMatchStatus.Finished: return 'Beendet';
      case LocalMatchStatus.Postponed: return 'Verschoben';
      case LocalMatchStatus.Cancelled: return 'Abgesagt';
      default: return 'Unbekannt';
    }
  };

  if (loading) {
    return <div style={loadingStyle}>Laden...</div>;
  }

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <h2 style={titleStyle}><CircleDot size={18} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 6 }} /> Lokale Spiele Datenmanagement</h2>
        <button onClick={handleStartCreate} style={createButtonStyle} disabled={isCreating}>
          <Plus size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} /> Neues lokales Spiel
        </button>
      </div>

      {error && (
        <div style={errorBannerStyle}>
          {error}
          <button onClick={() => setError(null)} style={errorCloseButtonStyle}><X size={14} /></button>
        </div>
      )}

      <div style={tableContainerStyle}>
        <table style={tableStyle}>
          <thead>
            <tr style={headerRowStyle}>
              <th style={headerCellStyle}>ID</th>
              <th style={headerCellStyle}>Spieltag</th>
              <th style={headerCellStyle}>Heimteam</th>
              <th style={headerCellStyle}>Auswärtsteam</th>
              <th style={headerCellStyle}>Ergebnis</th>
              <th style={headerCellStyle}>Geplant</th>
              <th style={headerCellStyle}>Tatsächlich</th>
              <th style={headerCellStyle}>Ort</th>
              <th style={headerCellStyle}>Schiedsrichter</th>
              <th style={headerCellStyle}>Dauer (Min)</th>
              <th style={headerCellStyle}>Status</th>
              <th style={headerCellStyle}>Aktiv</th>
              <th style={headerCellStyle}>Notizen</th>
              <th style={headerCellStyle}>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {/* Neue Zeile für das Erstellen */}
            {isCreating && (
              <tr style={{...rowStyle, backgroundColor: '#f0f9ff'}}>
                <td style={cellStyle}>
                  <span style={newBadgeStyle}>NEU</span>
                </td>

                {/* Spieltag */}
                <td style={cellStyle}>
                  <select
                    value={newMatchData.localMatchdayId || 0}
                    onChange={(e) => setNewMatchData({ ...newMatchData, localMatchdayId: Number(e.target.value) })}
                    style={selectStyle}
                  >
                    <option value={0}>Auswählen...</option>
                    {matchdays.map(md => (
                      <option key={md.id} value={md.id}>
                        {md.name} ({new Date(md.date).toLocaleDateString('de-DE')})
                      </option>
                    ))}
                  </select>
                </td>

                {/* Heimteam */}
                <td style={cellStyle}>
                  <input
                    type="text"
                    value={newMatchData.homeTeam || ''}
                    onChange={(e) => setNewMatchData({ ...newMatchData, homeTeam: e.target.value })}
                    style={inputStyle}
                    placeholder="Heimteam..."
                  />
                </td>

                {/* Auswärtsteam */}
                <td style={cellStyle}>
                  <input
                    type="text"
                    value={newMatchData.awayTeam || ''}
                    onChange={(e) => setNewMatchData({ ...newMatchData, awayTeam: e.target.value })}
                    style={inputStyle}
                    placeholder="Auswärtsteam..."
                  />
                </td>

                {/* Ergebnis */}
                <td style={cellStyle}>
                  <div style={scoreContainerStyle}>
                    <input
                      type="number"
                      min="0"
                      value={newMatchData.homeScore ?? ''}
                      onChange={(e) => setNewMatchData({ ...newMatchData, homeScore: e.target.value ? Number(e.target.value) : undefined })}
                      style={scoreInputStyle}
                      placeholder="H"
                    />
                    :
                    <input
                      type="number"
                      min="0"
                      value={newMatchData.awayScore ?? ''}
                      onChange={(e) => setNewMatchData({ ...newMatchData, awayScore: e.target.value ? Number(e.target.value) : undefined })}
                      style={scoreInputStyle}
                      placeholder="A"
                    />
                  </div>
                </td>

                {/* Geplantes Datum */}
                <td style={cellStyle}>
                  <input
                    type="datetime-local"
                    value={formatDateTime(newMatchData.scheduledDate)}
                    onChange={(e) => setNewMatchData({ ...newMatchData, scheduledDate: parseDateTime(e.target.value) })}
                    style={inputStyle}
                  />
                </td>

                {/* Tatsächliches Datum */}
                <td style={cellStyle}>
                  <input
                    type="datetime-local"
                    value={formatDateTime(newMatchData.actualDate)}
                    onChange={(e) => setNewMatchData({ ...newMatchData, actualDate: e.target.value ? parseDateTime(e.target.value) : undefined })}
                    style={inputStyle}
                  />
                </td>

                {/* Ort */}
                <td style={cellStyle}>
                  <input
                    type="text"
                    value={newMatchData.location || ''}
                    onChange={(e) => setNewMatchData({ ...newMatchData, location: e.target.value })}
                    style={inputStyle}
                    placeholder="Ort..."
                  />
                </td>

                {/* Schiedsrichter */}
                <td style={cellStyle}>
                  <input
                    type="text"
                    value={newMatchData.referee || ''}
                    onChange={(e) => setNewMatchData({ ...newMatchData, referee: e.target.value })}
                    style={inputStyle}
                    placeholder="Schiedsrichter..."
                  />
                </td>

                {/* Dauer */}
                <td style={cellStyle}>
                  <input
                    type="number"
                    min="0"
                    value={newMatchData.duration || ''}
                    onChange={(e) => setNewMatchData({ ...newMatchData, duration: e.target.value ? Number(e.target.value) : undefined })}
                    style={inputStyle}
                    placeholder="90"
                  />
                </td>

                {/* Status */}
                <td style={cellStyle}>
                  <select
                    value={newMatchData.status ?? LocalMatchStatus.Scheduled}
                    onChange={(e) => setNewMatchData({ ...newMatchData, status: Number(e.target.value) as LocalMatchStatus })}
                    style={selectStyle}
                  >
                    <option value={LocalMatchStatus.Scheduled}>Geplant</option>
                    <option value={LocalMatchStatus.Live}>Live</option>
                    <option value={LocalMatchStatus.Finished}>Beendet</option>
                    <option value={LocalMatchStatus.Postponed}>Verschoben</option>
                    <option value={LocalMatchStatus.Cancelled}>Abgesagt</option>
                  </select>
                </td>

                {/* Aktiv */}
                <td style={cellStyle}>
                  <input
                    type="checkbox"
                    checked={newMatchData.isActive ?? true}
                    onChange={(e) => setNewMatchData({ ...newMatchData, isActive: e.target.checked })}
                    style={checkboxStyle}
                  />
                </td>

                {/* Notizen */}
                <td style={cellStyle}>
                  <input
                    type="text"
                    value={newMatchData.notes || ''}
                    onChange={(e) => setNewMatchData({ ...newMatchData, notes: e.target.value })}
                    style={inputStyle}
                    placeholder="Notizen..."
                  />
                </td>

                {/* Aktionen */}
                <td style={cellStyle}>
                  <div style={actionsStyle}>
                    <button onClick={handleSaveNew} style={saveButtonStyle} title="Speichern">
                      <Check size={14} />
                    </button>
                    <button onClick={handleCancelCreate} style={cancelButtonStyle} title="Abbrechen">
                      <X size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            )}

            {/* Bestehende Spiele */}
            {matches.map((match) => (
              <tr key={match.id} style={rowStyle}>
                {editingId === match.id ? (
                  <>
                    {/* ID (nicht editierbar) */}
                    <td style={cellStyle}>{match.id}</td>

                    {/* Spieltag */}
                    <td style={cellStyle}>
                      <select
                        value={editData.localMatchdayId || 0}
                        onChange={(e) => setEditData({ ...editData, localMatchdayId: Number(e.target.value) })}
                        style={selectStyle}
                      >
                        <option value={0}>Auswählen...</option>
                        {matchdays.map(md => (
                          <option key={md.id} value={md.id}>
                            {md.name} ({new Date(md.date).toLocaleDateString('de-DE')})
                          </option>
                        ))}
                      </select>
                    </td>

                    {/* Heimteam */}
                    <td style={cellStyle}>
                      <input
                        type="text"
                        value={editData.homeTeam || ''}
                        onChange={(e) => setEditData({ ...editData, homeTeam: e.target.value })}
                        style={inputStyle}
                      />
                    </td>

                    {/* Auswärtsteam */}
                    <td style={cellStyle}>
                      <input
                        type="text"
                        value={editData.awayTeam || ''}
                        onChange={(e) => setEditData({ ...editData, awayTeam: e.target.value })}
                        style={inputStyle}
                      />
                    </td>

                    {/* Ergebnis */}
                    <td style={cellStyle}>
                      <div style={scoreContainerStyle}>
                        <input
                          type="number"
                          min="0"
                          value={editData.homeScore ?? ''}
                          onChange={(e) => setEditData({ ...editData, homeScore: e.target.value ? Number(e.target.value) : undefined })}
                          style={scoreInputStyle}
                        />
                        :
                        <input
                          type="number"
                          min="0"
                          value={editData.awayScore ?? ''}
                          onChange={(e) => setEditData({ ...editData, awayScore: e.target.value ? Number(e.target.value) : undefined })}
                          style={scoreInputStyle}
                        />
                      </div>
                    </td>

                    {/* Geplantes Datum */}
                    <td style={cellStyle}>
                      <input
                        type="datetime-local"
                        value={formatDateTime(editData.scheduledDate)}
                        onChange={(e) => setEditData({ ...editData, scheduledDate: parseDateTime(e.target.value) })}
                        style={inputStyle}
                      />
                    </td>

                    {/* Tatsächliches Datum */}
                    <td style={cellStyle}>
                      <input
                        type="datetime-local"
                        value={formatDateTime(editData.actualDate)}
                        onChange={(e) => setEditData({ ...editData, actualDate: e.target.value ? parseDateTime(e.target.value) : undefined })}
                        style={inputStyle}
                      />
                    </td>

                    {/* Ort */}
                    <td style={cellStyle}>
                      <input
                        type="text"
                        value={editData.location || ''}
                        onChange={(e) => setEditData({ ...editData, location: e.target.value })}
                        style={inputStyle}
                      />
                    </td>

                    {/* Schiedsrichter */}
                    <td style={cellStyle}>
                      <input
                        type="text"
                        value={editData.referee || ''}
                        onChange={(e) => setEditData({ ...editData, referee: e.target.value })}
                        style={inputStyle}
                      />
                    </td>

                    {/* Dauer */}
                    <td style={cellStyle}>
                      <input
                        type="number"
                        min="0"
                        value={editData.duration || ''}
                        onChange={(e) => setEditData({ ...editData, duration: e.target.value ? Number(e.target.value) : undefined })}
                        style={inputStyle}
                      />
                    </td>

                    {/* Status */}
                    <td style={cellStyle}>
                      <select
                        value={typeof editData.status === 'number' ? editData.status : LocalMatchStatus.Scheduled}
                        onChange={(e) => setEditData({ ...editData, status: Number(e.target.value) as LocalMatchStatus })}
                        style={selectStyle}
                      >
                        <option value={LocalMatchStatus.Scheduled}>Geplant</option>
                        <option value={LocalMatchStatus.Live}>Live</option>
                        <option value={LocalMatchStatus.Finished}>Beendet</option>
                        <option value={LocalMatchStatus.Postponed}>Verschoben</option>
                        <option value={LocalMatchStatus.Cancelled}>Abgesagt</option>
                      </select>
                    </td>

                    {/* Aktiv */}
                    <td style={cellStyle}>
                      <input
                        type="checkbox"
                        checked={editData.isActive ?? true}
                        onChange={(e) => setEditData({ ...editData, isActive: e.target.checked })}
                        style={checkboxStyle}
                      />
                    </td>

                    {/* Notizen */}
                    <td style={cellStyle}>
                      <input
                        type="text"
                        value={editData.notes || ''}
                        onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                        style={inputStyle}
                      />
                    </td>

                    {/* Aktionen */}
                    <td style={cellStyle}>
                      <div style={actionsStyle}>
                        <button onClick={() => handleSave(match.id)} style={saveButtonStyle} title="Speichern">
                          <Check size={14} />
                        </button>
                        <button onClick={handleCancelEdit} style={cancelButtonStyle} title="Abbrechen">
                          <X size={14} />
                        </button>
                      </div>
                    </td>
                  </>
                ) : (
                  <>
                    {/* Ansichtsmodus */}
                    <td style={cellStyle}>{match.id}</td>
                    <td style={cellStyle}>
                      {match.localMatchday ? `${match.localMatchday.name} (${new Date(match.localMatchday.date).toLocaleDateString('de-DE')})` : '-'}
                    </td>
                    <td style={cellStyle}>{match.homeTeam}</td>
                    <td style={cellStyle}>{match.awayTeam}</td>
                    <td style={cellStyle}>
                      {match.homeScore !== null && match.homeScore !== undefined && match.awayScore !== null && match.awayScore !== undefined
                        ? `${match.homeScore} : ${match.awayScore}`
                        : '-'}
                    </td>
                    <td style={cellStyle}>
                      {new Date(match.scheduledDate).toLocaleString('de-DE')}
                    </td>
                    <td style={cellStyle}>
                      {match.actualDate ? new Date(match.actualDate).toLocaleString('de-DE') : '-'}
                    </td>
                    <td style={cellStyle}>{match.location || '-'}</td>
                    <td style={cellStyle}>{match.referee || '-'}</td>
                    <td style={cellStyle}>{match.duration || '-'}</td>
                    <td style={cellStyle}>
                      <span style={getStatusBadgeStyle(match.status)}>
                        {getStatusText(match.status)}
                      </span>
                    </td>
                    <td style={cellStyle}>
                      <span style={match.isActive ? activeBadgeStyle : inactiveBadgeStyle}>
                        {match.isActive ? <Check size={14} /> : <X size={14} />}
                      </span>
                    </td>
                    <td style={cellStyle}>
                      <div style={notesCellStyle}>{match.notes || '-'}</div>
                    </td>
                    <td style={cellStyle}>
                      <div style={actionsStyle}>
                        <button onClick={() => handleEdit(match)} style={editButtonStyle} title="Bearbeiten">
                          <Pencil size={14} />
                        </button>
                        <button onClick={() => setDeleteConfirm(match.id)} style={deleteButtonStyle} title="Löschen">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div style={modalOverlayStyle} onClick={() => setDeleteConfirm(null)}>
          <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
            <h3 style={modalTitleStyle}>Lokales Spiel löschen?</h3>
            <p style={modalTextStyle}>
              Möchten Sie dieses lokale Spiel wirklich löschen? Diese Aktion kann nicht rückgängig gemacht werden.
            </p>
            <div style={modalActionsStyle}>
              <button onClick={() => setDeleteConfirm(null)} style={modalCancelButtonStyle}>
                Abbrechen
              </button>
              <button onClick={() => handleDelete(deleteConfirm)} style={modalDeleteButtonStyle}>
                Löschen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const getStatusBadgeStyle = (status: LocalMatchStatus | string | number | null | undefined): React.CSSProperties => {
  if (status === null || status === undefined) return inactiveBadgeStyle;
  const statusNum = typeof status === 'number' ? status : parseInt(status.toString());
  switch (statusNum) {
    case LocalMatchStatus.Scheduled: return scheduledBadgeStyle;
    case LocalMatchStatus.Live: return liveBadgeStyle;
    case LocalMatchStatus.Finished: return finishedBadgeStyle;
    case LocalMatchStatus.Postponed: return postponedBadgeStyle;
    case LocalMatchStatus.Cancelled: return cancelledBadgeStyle;
    default: return inactiveBadgeStyle;
  }
};

// Styles
const containerStyle: React.CSSProperties = { padding: '1rem' };
const headerStyle: React.CSSProperties = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' };
const titleStyle: React.CSSProperties = { margin: 0, fontSize: '1.5rem', color: '#2c3e50' };
const createButtonStyle: React.CSSProperties = { padding: '0.5rem 1rem', background: '#10b981', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '500' };
const loadingStyle: React.CSSProperties = { padding: '2rem', textAlign: 'center', color: '#6c757d' };
const tableContainerStyle: React.CSSProperties = { overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' };
const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' };
const headerRowStyle: React.CSSProperties = { backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' };
const headerCellStyle: React.CSSProperties = { padding: '0.75rem', textAlign: 'left', fontWeight: '600', color: '#495057', whiteSpace: 'nowrap' };
const rowStyle: React.CSSProperties = { borderBottom: '1px solid #e5e7eb' };
const cellStyle: React.CSSProperties = { padding: '0.75rem', verticalAlign: 'middle' };
const inputStyle: React.CSSProperties = { width: '100%', padding: '0.375rem', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '0.875rem' };
const selectStyle: React.CSSProperties = { width: '100%', padding: '0.375rem', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '0.875rem', backgroundColor: 'white' };
const checkboxStyle: React.CSSProperties = { width: '1.25rem', height: '1.25rem', cursor: 'pointer' };
const actionsStyle: React.CSSProperties = { display: 'flex', gap: '0.5rem' };
const editButtonStyle: React.CSSProperties = { padding: '0.25rem 0.5rem', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '1rem' };
const deleteButtonStyle: React.CSSProperties = { padding: '0.25rem 0.5rem', background: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '1rem' };
const saveButtonStyle: React.CSSProperties = { padding: '0.25rem 0.5rem', background: '#10b981', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '1rem' };
const cancelButtonStyle: React.CSSProperties = { padding: '0.25rem 0.5rem', background: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '1rem' };
const notesCellStyle: React.CSSProperties = { maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const activeBadgeStyle: React.CSSProperties = { padding: '0.25rem 0.5rem', borderRadius: '12px', fontSize: '0.75rem', background: '#d1fae5', color: '#065f46', fontWeight: '500' };
const inactiveBadgeStyle: React.CSSProperties = { padding: '0.25rem 0.5rem', borderRadius: '12px', fontSize: '0.75rem', background: '#fee2e2', color: '#991b1b', fontWeight: '500' };
const modalOverlayStyle: React.CSSProperties = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const modalStyle: React.CSSProperties = { backgroundColor: 'white', borderRadius: '8px', padding: '1.5rem', maxWidth: '400px', width: '90%', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' };
const modalTitleStyle: React.CSSProperties = { margin: '0 0 1rem 0', fontSize: '1.25rem', color: '#2c3e50' };
const modalTextStyle: React.CSSProperties = { margin: '0 0 1.5rem 0', color: '#6c757d' };
const modalActionsStyle: React.CSSProperties = { display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' };
const modalCancelButtonStyle: React.CSSProperties = { padding: '0.5rem 1rem', border: '1px solid #ced4da', borderRadius: '4px', background: 'white', color: '#495057', cursor: 'pointer' };
const modalDeleteButtonStyle: React.CSSProperties = { padding: '0.5rem 1rem', border: 'none', borderRadius: '4px', background: '#dc3545', color: 'white', cursor: 'pointer' };
const newBadgeStyle: React.CSSProperties = { padding: '0.25rem 0.5rem', borderRadius: '12px', fontSize: '0.7rem', background: '#10b981', color: 'white', fontWeight: 'bold' };
const errorBannerStyle: React.CSSProperties = { padding: '0.75rem 1rem', background: '#fee2e2', border: '1px solid #fecaca', borderRadius: '6px', color: '#dc2626', marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
const errorCloseButtonStyle: React.CSSProperties = { background: 'transparent', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '1.2rem', padding: '0 0.5rem' };

// Score input styles
const scoreContainerStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: '0.25rem' };
const scoreInputStyle: React.CSSProperties = { width: '3rem', padding: '0.375rem', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '0.875rem', textAlign: 'center' };

// Status badge styles
const scheduledBadgeStyle: React.CSSProperties = { padding: '0.25rem 0.5rem', borderRadius: '12px', fontSize: '0.75rem', background: '#fef3c7', color: '#92400e', fontWeight: '500' };
const liveBadgeStyle: React.CSSProperties = { padding: '0.25rem 0.5rem', borderRadius: '12px', fontSize: '0.75rem', background: '#dcfce7', color: '#166534', fontWeight: '500' };
const finishedBadgeStyle: React.CSSProperties = { padding: '0.25rem 0.5rem', borderRadius: '12px', fontSize: '0.75rem', background: '#dbeafe', color: '#1e40af', fontWeight: '500' };
const postponedBadgeStyle: React.CSSProperties = { padding: '0.25rem 0.5rem', borderRadius: '12px', fontSize: '0.75rem', background: '#e0e7ff', color: '#3730a3', fontWeight: '500' };
const cancelledBadgeStyle: React.CSSProperties = { padding: '0.25rem 0.5rem', borderRadius: '12px', fontSize: '0.75rem', background: '#fee2e2', color: '#991b1b', fontWeight: '500' };

export default LocalMatchesDataTable;

